import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  GiftIcon,
  LinkIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { FullPageSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import PlatformClientStateBreakdown from './PlatformClientStateBreakdown';

function PlatformCompanyDetailView({
  companyId,
  onBack,
  onConfigure,
  onViewUsers,
  onViewProspects,
}) {
  const { t } = useLanguage();
  const pa = t.platformAdmin;
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`/api/admin/platform/companies/${companyId}`, { withCredentials: true });
      setDetail(response.data);
    } catch (error) {
      console.error('Failed to load company detail', error);
      toast.error(pa.failedToLoadCompany);
    } finally {
      setLoading(false);
    }
  }, [companyId, pa.failedToLoadCompany]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return <FullPageSkeleton />;
  }

  if (!detail?.company) {
    return (
      <EmptyState
        icon={BuildingOffice2Icon}
        title={pa.companyNotFound}
        description={pa.companyNotFoundDesc}
        action={(
          <button type="button" onClick={onBack} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white">
            {pa.backToCompanies}
          </button>
        )}
      />
    );
  }

  const { company, admins, top_vendors, top_clients, recent_clients, recent_prospects, client_states } = detail;
  const stats = company.stats || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-xl font-bold text-white shadow-glow">
            {company.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-secondary-900 ">{company.name}</h2>
            <p className="text-sm text-secondary-500 ">{company.slug}</p>
            <p className="mt-1 text-xs text-secondary-500 ">
              {pa.registeredOn} {new Date(company.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={onBack}>{pa.backToCompanies}</Button>
          <Button variant="secondary" onClick={() => onViewUsers?.(company.id)}>{pa.viewCompanyUsers}</Button>
          <Button variant="secondary" onClick={() => onViewProspects?.(company.id)}>{pa.viewCompanyProspects}</Button>
          <Button onClick={() => onConfigure?.(company.id)}>{pa.configure}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        {[
          { label: pa.admins, value: stats.admins, icon: ShieldCheckIcon, color: 'text-amber-600' },
          { label: pa.vendorsShort, value: stats.vendors, icon: UserGroupIcon, color: 'text-red-600' },
          { label: pa.clientsShort, value: stats.clients, icon: UsersIcon, color: 'text-emerald-600' },
          { label: pa.prospectsShort, value: stats.prospects, icon: ClipboardDocumentListIcon, color: 'text-cyan-600' },
          { label: pa.referralsShort, value: stats.referrals, icon: LinkIcon, color: 'text-orange-600' },
          { label: pa.revenue, value: `$${(stats.revenue || 0).toLocaleString()}`, icon: CurrencyDollarIcon, color: 'text-emerald-600' },
          { label: pa.pendingGifts, value: stats.pending_gifts || 0, icon: GiftIcon, color: 'text-pink-600' },
        ].map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="card-premium p-4"
            >
              <div className="mb-2 flex items-center gap-2 text-secondary-500 ">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">{item.label}</span>
              </div>
              <p className={`text-2xl font-bold tabular-nums ${item.color}`}>{item.value}</p>
            </motion.div>
          );
        })}
      </div>

      <section className="card-premium p-5">
        <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.clientStateBreakdown}</h3>
        <PlatformClientStateBreakdown clientStates={client_states || {}} />
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="card-premium p-5">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.companyAdmins}</h3>
          <div className="space-y-3">
            {admins?.length ? admins.map((admin) => (
              <div key={admin.id} className="rounded-xl border border-secondary-200/80 p-3 ">
                <p className="font-medium text-secondary-900 ">{admin.username}</p>
                <p className="text-sm text-secondary-500 ">{admin.email}</p>
              </div>
            )) : (
              <p className="text-sm text-secondary-500 ">{pa.noCompanyAdmins}</p>
            )}
          </div>
        </section>

        <section className="card-premium p-5">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.topVendors}</h3>
          <div className="space-y-3">
            {top_vendors?.length ? top_vendors.map((vendor, index) => (
              <div key={vendor.id} className="flex items-center justify-between rounded-xl border border-secondary-200/80 p-3 ">
                <div>
                  <p className="font-medium text-secondary-900 ">{index + 1}. {vendor.username}</p>
                  <p className="text-xs text-secondary-500 ">{vendor.clients_count} {pa.clientsShort}</p>
                </div>
                <p className="text-sm font-semibold text-orange-600">{vendor.referrals_count} {pa.referralsShort}</p>
              </div>
            )) : (
              <p className="text-sm text-secondary-500 ">{pa.noVendorsYet}</p>
            )}
          </div>
        </section>

        <section className="card-premium p-5">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.topClients}</h3>
          <div className="space-y-3">
            {top_clients?.length ? top_clients.map((client, index) => (
              <div key={client.id} className="flex items-center justify-between rounded-xl border border-secondary-200/80 p-3 ">
                <div>
                  <p className="font-medium text-secondary-900 ">{index + 1}. {client.username}</p>
                  <p className="text-xs capitalize text-secondary-500 ">{client.state}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-success-600">{client.referrals_count} {pa.referralsShort}</p>
                  <p className="text-xs text-secondary-500 ">{client.reward_points} {pa.rewardPoints}</p>
                </div>
              </div>
            )) : (
              <p className="text-sm text-secondary-500 ">{pa.noActiveClientsYet}</p>
            )}
          </div>
        </section>

        <section className="card-premium p-5">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.recentProspects}</h3>
          <div className="space-y-3">
            {recent_prospects?.length ? recent_prospects.map((prospect) => (
              <div key={prospect.id} className="rounded-xl border border-secondary-200/80 p-3 ">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-secondary-900 ">{prospect.name}</p>
                  <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-secondary-600  ">
                    {prospect.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-secondary-500 ">
                  {prospect.vendor_username || '—'} · {prospect.phone || prospect.email || '—'}
                </p>
              </div>
            )) : (
              <p className="text-sm text-secondary-500 ">{pa.noProspectsYet}</p>
            )}
          </div>
        </section>
      </div>

      <section className="card-premium overflow-hidden">
        <div className="border-b border-secondary-200/70 px-5 py-4 ">
          <h3 className="text-lg font-semibold text-secondary-900 ">{pa.recentClients}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200 ">
            <thead className="bg-secondary-50 ">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.client}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.clientState}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.rewardPoints}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">{pa.registeredOn}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200 ">
              {(recent_clients || []).length > 0 ? (
                recent_clients.map((client) => (
                  <tr key={client.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-secondary-900 ">{client.username}</p>
                      <p className="text-xs text-secondary-500 ">{client.email}</p>
                    </td>
                    <td className="px-4 py-3 capitalize text-sm text-secondary-700 ">{client.state}</td>
                    <td className="px-4 py-3 text-sm text-secondary-700 ">{client.reward_points}</td>
                    <td className="px-4 py-3 text-sm text-secondary-600 ">
                      {new Date(client.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-secondary-500 ">
                    {pa.noRecentClients}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default PlatformCompanyDetailView;
